import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { LogOut, Menu, X, Shield, Eye, ChevronDown, ChevronRight, Home as HomeIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { PAGE_GROUPS, hasGroupAccess, findGroupForPath, accessibleItems, groupLandingHref } from "@/lib/pagePermissions";

export function Layout({ children, fullWidth = false }: { children: ReactNode; fullWidth?: boolean }) {
  const [location] = useLocation();
  const { user, logout, isRealAdmin, viewAsUser, setViewAsUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const visibleGroups = PAGE_GROUPS.filter((g) => !g.navHidden && hasGroupAccess(user, g));
  const activeGroup = findGroupForPath(location);

  const renderNav = (onNavigate?: () => void) => (
    <>
      <Link
        href="/"
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-xl transition-all group relative",
          location === "/"
            ? "bg-primary/10 text-primary font-semibold"
            : "text-duffells-grey hover:bg-slate-50 hover:text-foreground"
        )}
      >
        <HomeIcon
          className={cn("w-5 h-5", location === "/" ? "text-primary" : "text-duffells-grey/60 group-hover:text-duffells-grey")}
        />
        Home
      </Link>

      {visibleGroups.map((group) => {
        const Icon = group.icon;
        const isActive = activeGroup?.key === group.key;
        const isExpanded = isActive;
        // Filter to items the current user can see (e.g. non-admins in the
        // Expenses group only see Receipts). Groups with 0 visible items
        // are skipped entirely.
        const visibleItems = accessibleItems(user, group);
        if (visibleItems.length === 0) return null;
        const landing = groupLandingHref(user, group);
        // Single-item (after filtering) groups behave like plain links;
        // multi-item groups collapse and expand based on whether the user
        // is inside them.
        const isMulti = visibleItems.length > 1;
        return (
          <div key={group.key}>
            <Link
              href={landing}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all group relative",
                isActive
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-duffells-grey hover:bg-slate-50 hover:text-foreground"
              )}
            >
              <Icon
                className={cn(
                  "w-5 h-5 transition-colors",
                  isActive ? "text-primary" : "text-duffells-grey/60 group-hover:text-duffells-grey"
                )}
              />
              <span className="flex-1">{group.label}</span>
              {isMulti && (
                isExpanded
                  ? <ChevronDown className="w-4 h-4 text-duffells-grey/40" />
                  : <ChevronRight className="w-4 h-4 text-duffells-grey/40" />
              )}
              {isActive && (
                <motion.div
                  layoutId="sidebar-active"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full"
                />
              )}
            </Link>
            {isMulti && isExpanded && (
              <div className="ml-6 mt-1 mb-1 space-y-1 border-l border-slate-200 pl-3">
                {visibleItems.map((child) => {
                  const childActive =
                    location === child.href || location.startsWith(child.href + "/");
                  const ChildIcon = child.icon;
                  return (
                    <Link
                      key={child.href}
                      href={child.href}
                      onClick={onNavigate}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                        childActive
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-duffells-grey/80 hover:bg-slate-50 hover:text-duffells-grey"
                      )}
                    >
                      <ChildIcon className={cn("w-4 h-4", childActive ? "text-primary" : "text-duffells-grey/50")} />
                      {child.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full z-10">
        <div className="p-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-duffells-orange flex items-center justify-center shadow-lg shadow-primary/20">
            <span className="text-white font-bold text-xl">D</span>
          </div>
          <span className="font-display font-bold text-xl tracking-tight text-duffells-grey">Duffells</span>
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto mt-4">{renderNav()}</nav>

        <div className="p-4 border-t border-slate-100">
          <div className="px-4 py-3 bg-slate-50 rounded-xl mb-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-duffells-grey truncate">{user?.name}</p>
                <p className="text-xs text-duffells-grey/60 truncate">{user?.email}</p>
              </div>
              {isAdmin ? (
                <span className="ml-2 flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-duffells-orange/10 text-duffells-orange">
                  <Shield className="w-2.5 h-2.5" />
                  Admin
                </span>
              ) : isRealAdmin ? (
                <span
                  className="ml-2 flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-duffells-grey"
                  title="You're previewing the user experience. Backend permissions are unchanged."
                >
                  <Eye className="w-2.5 h-2.5" />
                  User view
                </span>
              ) : null}
            </div>
          </div>

          {isRealAdmin && (
            <label
              className="flex items-center justify-between gap-3 px-4 py-2.5 mb-2 text-sm font-medium text-duffells-grey rounded-xl hover:bg-slate-50 cursor-pointer select-none"
              title="Hide admin features so you can see what a regular user sees. Your real permissions on the server are unchanged."
            >
              <span className="flex items-center gap-3">
                <Eye className="w-4 h-4 text-duffells-grey/60" />
                View as user
              </span>
              <span className="relative inline-flex h-5 w-9 flex-shrink-0">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={viewAsUser}
                  onChange={(e) => setViewAsUser(e.target.checked)}
                  data-testid="toggle-view-as-user"
                />
                <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors peer-checked:bg-duffells-orange" />
                <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
              </span>
            </label>
          )}

          <button
            onClick={() => logout()}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-duffells-grey rounded-xl hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-20 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-duffells-orange flex items-center justify-center">
            <span className="text-white font-bold text-lg">D</span>
          </div>
          <span className="font-display font-bold text-lg text-duffells-grey">Duffells</span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 -mr-2 text-duffells-grey">
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden fixed top-16 left-0 right-0 bg-white border-b border-slate-200 z-10 p-4 shadow-xl"
          >
            <nav className="space-y-1">
              {renderNav(() => setIsMobileMenuOpen(false))}
              {isRealAdmin && (
                <label className="flex items-center justify-between gap-3 px-4 py-3 text-duffells-grey rounded-xl cursor-pointer select-none">
                  <span className="flex items-center gap-3">
                    <Eye className="w-5 h-5" />
                    View as user
                  </span>
                  <span className="relative inline-flex h-5 w-9 flex-shrink-0">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={viewAsUser}
                      onChange={(e) => setViewAsUser(e.target.checked)}
                    />
                    <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors peer-checked:bg-duffells-orange" />
                    <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </span>
                </label>
              )}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  logout();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 rounded-xl"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 md:ml-64 pt-16 md:pt-0 min-h-screen">
        <div className={cn(fullWidth ? "w-full" : "max-w-7xl mx-auto", "p-4 sm:p-6 lg:p-8")}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
